import type { StoreShippingFormOption } from './storeCheckoutShippingTypeUpdate.utils';
import type { ICartItemDetail } from '@/common/interfaces/cart.interface';

export type StoreCartMoqShippingLane = 'standard' | 'individually_shipped';

export const DEFAULT_STORE_CART_MOQ_SHIPPING_LANE: StoreCartMoqShippingLane = 'standard';

export const mapStoreShippingFormOptionToMoqLane = (
  shippingOption: StoreShippingFormOption | '' | undefined
): StoreCartMoqShippingLane =>
  shippingOption === 'direct' ? 'individually_shipped' : DEFAULT_STORE_CART_MOQ_SHIPPING_LANE;

export const normalizeStoreCartMoqShippingLane = (
  shippingLane: unknown
): StoreCartMoqShippingLane =>
  shippingLane === 'individually_shipped'
    ? 'individually_shipped'
    : DEFAULT_STORE_CART_MOQ_SHIPPING_LANE;

/** True when any design-group MOQ differs between two successful fetch snapshots. */
export const haveStoreCartDesignGroupMoqsChanged = (
  previousMoqs: Record<string, number>,
  nextMoqs: Record<string, number>
): boolean => {
  if (Object.keys(previousMoqs).length === 0 || Object.keys(nextMoqs).length === 0) {
    return false;
  }

  const groupKeys = new Set([...Object.keys(previousMoqs), ...Object.keys(nextMoqs)]);

  return Array.from(groupKeys).some((groupKey) => previousMoqs[groupKey] !== nextMoqs[groupKey]);
};

export const buildStoreCheckoutItemPriceSnapshot = (
  cartItems: ICartItemDetail[]
): Record<number, number> =>
  Object.fromEntries(
    cartItems
      .filter(
        (item) => typeof item.id === 'number' && typeof item.price === 'number' && item.price > 0
      )
      .map((item) => [item.id, item.price as number])
  );

/** True when any tracked item unit price changed after a shipping-option switch. */
export const haveStoreCheckoutItemPricesChanged = (
  previousPricesByItemId: Record<number, number>,
  nextItems: ICartItemDetail[]
): boolean => {
  if (Object.keys(previousPricesByItemId).length === 0) {
    return false;
  }

  return nextItems.some((item) => {
    const previousPrice = previousPricesByItemId[item.id];

    if (previousPrice == null || typeof item.price !== 'number' || item.price <= 0) {
      return false;
    }

    return previousPrice !== item.price;
  });
};
